import axiosInstance from "@/utils/http";
import { defineStore } from "pinia";
import * as utils from '@/utils/utils';
import { EventSourcePolyfill } from "event-source-polyfill";
import { RFQ_LS_KEY, RFQ_QUOTE_TIMER_MS } from "@/config/rfqConfig.js";
import { useSnackbarStore } from '@/store/index.js';
import { i18n } from "@/plugins/vuetify";

const axios = axiosInstance;

//export const RFQ_PATH = "http://localhost:8000/apiv1.3/octax/rfq"; //=> ambiente dev SEM BUILDRUN
export const RFQ_PATH = "/apiv1.3/octax/rfq"; // producao
//export const SECRETKEY = "-1";
export const SECRETKEY = "9d4df61eb41aef7bfa7996496d0014af";
export const RFQ_VENUE_ID = "43633296000190";
export const RFQ_VENUE_SECRET = "IR73iLHhwL";
export const RFQ_TEXT = "REMESSA";

export const usePlexusStore = defineStore("plexus", {
  state: () => ({
    // Persisted RFQ data
    rfqList: JSON.parse(localStorage.getItem(RFQ_LS_KEY) || "[]"),
    rfqId: null,
    selectedQuote: JSON.parse(localStorage.getItem("selectedQuote") || "{}"),

    // Trading form state
    side: "BUY",
    ccy: "USD",
    settlement: "d+0",
    activeTab: "fxSpot",
    maturityDate: "",
    amount: "0",
    amountFieldKey: 0,

    // Quote / grid state
    gridActive: false,
    loadingNewRfq: false,
    quote: null,
    rfqSecurity: null,
    rfqSymbol: null,
    eventSource: null,
    rfqCancellationHandled: false,
    rfqCancelEmailEligible: false,
    cancelledOrder: false,
    successOrder: false,
    isManualClose: false,
    currentQuote: null,
    showTimer: false,
    showQuoteConfirmationDialog: false,
    mode: "make",
  }),

  getters: {
    getRfqList: (state) => state.rfqList,

    numericAmount: (state) => utils.convertCurrencyToNumber(state.amount),

    isSpot: (state) => {
      const quote = state.quote?.[0];
      if (quote?.rfq_security) return quote.rfq_security === "FXSPOT";
      return state.activeTab.toUpperCase().includes("FXSPOT");
    },

    disableQuoteButton: (state) => utils.convertCurrencyToNumber(state.amount) === 0.0 || state.gridActive,

    disableGrid: (state) => !state.gridActive,

    bestRate: (state) => {
      const rfqBase = state.quote?.[0];
      if (!rfqBase?.rfq_quote?.length) return null;
      const isBuy = state.side?.toUpperCase() === "BUY";
      const best = rfqBase.rfq_quote.reduce((prev, current) => {
        if (!prev) return current;
        const curPx = Number(current.rfq_quote_px_client);
        const prevPx = Number(prev.rfq_quote_px_client);
        if (!isFinite(curPx)) return prev;
        if (!isFinite(prevPx)) return current;
        return isBuy
          ? curPx < prevPx ? current : prev
          : curPx > prevPx ? current : prev;
      }, undefined);
      if (!best) return null;
      return { ...best, rfq_id: rfqBase.rfq_id, security: rfqBase.rfq_security };
    },

    quoteList: (state) => {
      if (!state.quote) {
        return [
          {
            rfq_id: "",
            rfq_status: "",
            rfq_cancel_reason: "",
            rfq_quote: [
              {
                rfq_quote_time: "-",
                rfq_quote_time_s: "-1",
                rfq_quote_px_client: "-",
                rfq_quote_spread_avg: "-",
                rfq_quote_spread_ptax: "-",
                rfq_quote_fwdpoints: "-",
                rfq_quote_spotrate: "-",
                rfq_quote_exptime: "-",
                rfq_quote_exptime_s: "-1",
                counterparty_name: "-",
              },
            ],
          },
        ];
      }
      return state.quote;
    },

    rfqElements: (state) => {
      return ({ activeTab, orderlogFilter }) => {
        const list = Array.isArray(state.rfqList) ? state.rfqList : [];
        const securityType = activeTab === "ndf" ? "FXNDF" : "FXSPOT";
        const bySecurity = list.filter(
          (item) => (item.rfq_security || "FXSPOT") === securityType,
        );
        const filtered =
          orderlogFilter === "all"
            ? bySecurity
            : bySecurity.filter((item) => item.rfq_status === orderlogFilter);

        return filtered.map((item) => ({
          rfq_id: item.rfq_id,
          rfq_status: item.rfq_status || "-",
          rfq_ccy: item.rfq_orderqty_ccy || "-",
          rfq_side: item.rfq_side || "-",
          rfq_amount: utils.formatMoneyAmount(item.rfq_orderqty),
          rfq_total: item.rfq_status === 'DEAL' ? (Number(item.rfq_orderqty) * Number(item.rfq_deal_px)).toFixed(2) : 0 ,
          rfq_spotsett: item.rfq_settlement
            ? item.rfq_settlement.toUpperCase()
            : "-",
          rfq_security: item.rfq_security || "FXSPOT",
          rfq_parity: item.rfq_orderqty_ccy + "/BRL",
          rfq_maturity: item.rfq_settdate_ccy2.slice(2) || "-",
          rfq_deal_time: item.rfq_deal_time_s || item.saved_at || "-",
          rfq_deal_px: item.rfq_deal_px?.toFixed(4) || "-",
          bank_label: item.bank_label || "-",
          rfq_quote_forward_rate: item?.rfq_deal_px?.toFixed(4) || '-',
          rfq_quote_spotrate: !isNaN(Number(item.rfq_quote_spotrate)?.toFixed(4)) ?  Number(item.rfq_quote_spotrate)?.toFixed(4) : "-",
          rfq_quote_fwdpoints: !isNaN(Number(item.rfq_quote_fwdpoints)?.toFixed(4)) ?  Number(item.rfq_quote_fwdpoints)?.toFixed(4) : "-",
          rfq_spread_ptax: item.rfq_spread_ptax || "-",
        }));
      };
    },
  },

  actions: {
    persistRfqList() {
      localStorage.setItem(RFQ_LS_KEY, JSON.stringify(this.rfqList));
    },

    addRfqOrder(order) {
      this.rfqList = [order, ...this.rfqList];
      this.persistRfqList();
    },

    removeRfqOrder(rfqId) {
      this.rfqList = this.rfqList.filter((order) => order.rfq_id !== rfqId);
      this.persistRfqList();
    },

    addCancelledRfq(order) {
      const alreadyExists = this.rfqList.some(
        (item) => item.rfq_id === order.rfq_id,
      );
      if (!alreadyExists) {
        this.rfqList = [order, ...this.rfqList];
        this.persistRfqList();
      }
    },

    saveOnRfqList(orders) {
      this.rfqList = Array.isArray(orders) ? [...orders] : [];
      this.persistRfqList();
    },

    shouldShowExpiredToast() {
      const expiration = this.currentQuote?.expiration;
      if (!expiration) return false;
      return Date.now() >= expiration;
    },

    async startNewQuoteTimer(expirationMs = null, expirationAt = null) {
      const timerMs = Number.isFinite(expirationMs) ? expirationMs : RFQ_QUOTE_TIMER_MS;
      const expiration = Number.isFinite(expirationAt) ? expirationAt : Date.now() + timerMs;
      const remainingMs = Math.max(0, expiration - Date.now());
      this.currentQuote = {
        rfq_id: this.rfqId,
        expiration,
        originalExpiration: expiration,
        extended: false,
      };
      this.showTimer = true;
      this.rfqCancellationHandled = false;
      return remainingMs;
    },

    resetGrid() {
      this.showTimer = false;
      this.gridActive = false;
      this.quote = null;
      this.rfqId = null;
      this.rfqSecurity = null;
      this.rfqSymbol = null;
      this.successOrder = false;
      this.cancelledOrder = false;
      this.isManualClose = false;
      this.rfqCancelEmailEligible = false;
      this.rfqCancellationHandled = false;
      this.selectedQuote = undefined;
      this.amount = "0";
      this.amountFieldKey++;
      this.showQuoteConfirmationDialog = false;
      this.currentQuote = null;
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    },

    getQuoteFeedHeartbeatTimeoutMs() {
      const expiration = this.currentQuote?.expiration;
      if (!expiration) return null;
      const remaining = expiration - Date.now();
      if (!Number.isFinite(remaining) || remaining <= 0) return null;
      return Math.max(remaining, 1000);
    },

    async getRfqs(payload) {
      try {
        const url = `${RFQ_PATH}/new`;
        return axios.post(url, payload, {
          headers: { SECRETKEY: SECRETKEY },
        });
      } catch (e) {
        console.error("ERROR HERE: ", e.response?.message, e.response);
      }
    },

    async makeQuote(amount, side, ccy, settlement, activeTab, maturityDate) {
      this.amount = amount;
      this.side = side;
      this.ccy = ccy;
      this.settlement = settlement;
      this.activeTab = activeTab;
      this.maturityDate = maturityDate;

      const parity = `${ccy}/BRL`;
      const quotePayload = await this.makeQuotePayload(
        amount,
        side,
        ccy,
        settlement,
        parity,
        activeTab,
        maturityDate,
      );
      const timeoutMs = this.getQuoteFeedHeartbeatTimeoutMs();
      await this.requestRfq(quotePayload, timeoutMs);
    },

    async requestRfq(quotePayload, timeoutMs) {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      let resp;
      const snackbar = useSnackbarStore();
      try {
        resp = await this.getRfqs(quotePayload);
      } catch (e) {
        console.error("RFQ request failed", e?.message);
        this.loadingNewRfq = false;
        snackbar.open("Network error, please try again.", "red");
        return;
      }
      if (!resp?.data) {
        this.loadingNewRfq = false;
        snackbar.open("Network error, please try again.", "red");
        return;
      }
      const data = resp.data;
      this.rfqId = data.rfq_id;
      if (data.rfq_status === "REJECTED") {
        this.loadingNewRfq = false;
        snackbar.open(
          `RFQ rejeitado: ${
            data.rfq_reject_reason || data.rfq_reject_text || "REJECTED"
          }`,
          "red",
        );
        this.lsSaveCancelledRfq(this.rfqId);
        return;
      }

      this.rfqSecurity = data.rfq_security;
      this.rfqSymbol = data.rfq_symbol;
      const activeRfqId = data.rfq_id;
      this.eventSource = this.getQuoteFeed(
        data.rfq_id,
        (quoteData) => {
          this.onQuote(quoteData);
        },
        (_error) => {
          const snackbar = useSnackbarStore();
          if (this.rfqId !== activeRfqId) return;
          const shouldShowExpired = this.shouldShowExpiredToast();
          this.cancelledOrder = true;
          if (this.eventSource) {
            this.eventSource.close();
          }
          if (!shouldShowExpired) return;
          this.isManualClose = false;
          snackbar.open(i18n.t("extras.quoteExpired"), "attention");
        },
        timeoutMs,
      );
    },

    async onQuote(data) {
      const snackbar = useSnackbarStore();
      const rfqId = data.rfq_id ?? this.rfqId;

      if (data.rfq_status === "CANCELLED" || data.rfq_status === "TIMEOUT") {
        if (this.eventSource) this.eventSource.close();
        const reason = data.rfq_cancel_reason || data.rfq_status;
        snackbar.open(`${i18n.t("extras.quoteExpired")}: ${reason}`, "attention");
        this.resetGrid();
        return;
      }

      // Full RFQ object: rfq_quote is already the complete current bank list — replace in place.
      // Single flat bank quote: merge into existing list by rfq_quote_id.
      let quotes;
      if (Array.isArray(data.rfq_quote)) {
        quotes = data.rfq_quote;
      } else {
        const item = utils.formmatingQuote(data);
        const existing = this.quote?.[0]?.rfq_quote ?? [];
        const idx = existing.findIndex((q) => q.rfq_quote_id === item.rfq_quote_id);
        quotes = idx >= 0
          ? existing.map((q, i) => (i === idx ? item : q))
          : [...existing, item];
      }

      this.quote = [
        {
          rfq_id: rfqId,
          rfq_status: data.rfq_status,
          rfq_cancel_reason: data.rfq_cancel_reason || null,
          rfq_security: data.rfq_security ?? this.rfqSecurity ?? (this.activeTab === "ndf" ? "FXNDF" : "FXSPOT"),
          rfq_symbol: data.rfq_symbol ?? this.rfqSymbol ?? `${this.ccy}/BRL`,
          rfq_side: data.rfq_side ?? this.side,
          rfq_orderqty: data.rfq_orderqty,
          rfq_orderqty_ccy: data.rfq_orderqty_ccy,
          rfq_settdate_ccy1: data.rfq_settdate_ccy1 || "-",
          rfq_settdate_ccy2: data.rfq_settdate_ccy2 || "-",
          rfq_settlement: data.rfq_settlement ?? (this.activeTab === "fxSpot" ? this.settlement : this.maturityDate),
          rfq_quote: quotes,
        },
      ];

      if (!this.showQuoteConfirmationDialog && this.bestRate) {
        this.selectedQuote = this.bestRate;
        localStorage.setItem("selectedQuote", JSON.stringify(this.selectedQuote));
      }
      if (!this.gridActive) {
        this.gridActive = true;
        this.loadingNewRfq = false;
        await this.startNewQuoteTimer(RFQ_QUOTE_TIMER_MS);
      }
    },

    async makeOrder() {
      const data = await this.formatAndOrder(this.selectedQuote);
      await this.handleOrderResponse(data);
    },

    lsSaveOrder(dealData) {
      const order = {
        rfq_id: dealData.rfq_id,
        rfq_status: "DEAL",
        rfq_deal_px: dealData.rfq_deal_px,
        rfq_deal_symbol: dealData.rfq_deal_symbol,
        rfq_deal_time_s: utils.formatDateTimeBR(new Date().toISOString()),
        rfq_side: this.side,
        rfq_orderqty: this.numericAmount,
        rfq_orderqty_ccy: this.ccy,
        rfq_settlement:
          (this.activeTab === "ndf" ? this.maturityDate : this.settlement) || "-",
        rfq_security:
          this.rfqSecurity || (this.activeTab === "ndf" ? "FXNDF" : "FXSPOT"),
        bank_label:
          (this.selectedQuote &&
            (this.selectedQuote.bank_name ||
              this.selectedQuote.counterparty_name)) ||
          "Banco Fibra",
        rfq_spread_ptax: this.selectedQuote?.rfq_quote_spread_ptax || "-",
        rfq_settdate_ccy2: this.maturityDate ?? "-",
        saved_at: utils.formatDateTimeBR(new Date().toISOString()),
        rfq_quote_fwdpoints: this.selectedQuote?.rfq_quote_fwdpoints || "-",
        rfq_quote_spotrate:
          this.selectedQuote?.rfq_quote_spotrate ||
          this.selectedQuote?.rfq_quote_px_client ||
          "-",
      };
      this.addRfqOrder(order);
    },

    lsRemoveOrder(rfqId) {
      this.removeRfqOrder(rfqId);
    },

    lsSaveCancelledRfq(rfqId) {
      const order = {
        rfq_id: rfqId,
        rfq_status: "DECLINED",
        rfq_side: this.side,
        rfq_orderqty: this.numericAmount,
        rfq_orderqty_ccy: this.ccy,
        rfq_settlement:
          (this.activeTab === "ndf" ? this.maturityDate : this.settlement) || "-",
        rfq_settdate_ccy2: this.maturityDate ?? "-",
        rfq_spread_ptax: this.selectedQuote?.rfq_quote_spread_ptax || "-",
        rfq_security:
          this.rfqSecurity || (this.activeTab === "ndf" ? "FXNDF" : "FXSPOT"),
        bank_label:
          (this.selectedQuote &&
            (this.selectedQuote.bank_name ||
              this.selectedQuote.counterparty_name)) ||
          "-",
        saved_at: utils.formatDateTimeBR(new Date().toISOString()),
      };
      this.addCancelledRfq(order);
    },

    async handleOrderResponse(data) {
      const snackbar = useSnackbarStore();
      if (data.rfq_status === "REJECTED" || data.rfq_status === "CANCELLED") {
        this.cancelledOrder = true;
        this.lsSaveCancelledRfq(data.rfq_id);
        await this.closeQuoteFeed(data.rfq_id);
        if (this.eventSource) this.eventSource.close();
        this.resetGrid();
        return;
      }
      this.lsSaveOrder(data);
      this.successOrder = true;
      snackbar.open("Success");
      if (this.eventSource) this.eventSource.close();
      this.resetGrid();
    },

    async declineQuote(rejectMessage = "Quote declined") {
      const snackbar = useSnackbarStore();
      if (this.rfqCancellationHandled) return;
      this.rfqCancellationHandled = true;

      const activeQuote = this.quote && this.quote[0];
      const rfqId = (activeQuote && activeQuote.rfq_id) || this.rfqId;

      this.lsSaveCancelledRfq(rfqId);
      this.mode = "make";
      snackbar.open(rejectMessage, "red");

      await this.closeQuoteFeed(rfqId);
      this.resetGrid();
    },

    async makeQuotePayload(
      selectedAmount,
      side,
      ccy,
      settlement,
      parity,
      product,
      maturityDate = null,
    ) {
      const amount = utils.convertCurrencyToNumber(selectedAmount);
      const now = Date.now();
      const isNdf = product?.toUpperCase().includes("NDF");

      let settdateCcy1;
      let settdateCcy2;

      if (isNdf) {
        const dueDateStr = /^\d{4}-\d{2}-\d{2}/.test(maturityDate || "")
          ? utils.toYYYYMMDD(utils.parseISODate(maturityDate.slice(0, 10)))
          : utils.getDateYYYYMMDD(30);
        const [y, mo, d] = [
          parseInt(dueDateStr.slice(0, 4)),
          parseInt(dueDateStr.slice(4, 6)) - 1,
          parseInt(dueDateStr.slice(6, 8)),
        ];
        settdateCcy2 = dueDateStr;
        settdateCcy1 = utils.toYYYYMMDD(
          utils.getPreviousBusinessDay(new Date(y, mo, d)),
        );
      } else {
        const spotDaysMap = { "d+0": 0, "d+1": 1, "d+2": 2 };
        const days = spotDaysMap[settlement?.toLowerCase()] ?? 0;
        settdateCcy1 = utils.getDateYYYYMMDD(days);
        settdateCcy2 = utils.getDateYYYYMMDD(days);
      }

      const payload = {
        rfq_security: isNdf ? "FXNDF" : "FXSPOT",
        rfq_symbol: parity,
        rfq_side: side,
        rfq_orderqty: amount,
        rfq_orderqty_ccy: ccy,
        rfq_settdate_ccy1: settdateCcy1,
        rfq_settdate_ccy2: settdateCcy2,
        rfq_taker_id: "43633296000190",
        rfq_taker_time: now / 1000,
        rfq_venue_id: RFQ_VENUE_ID,
        rfq_venue_secret: RFQ_VENUE_SECRET,
        rfq_text: RFQ_TEXT,
       // rfq_settlement: settlement,
      };

      payload.rfq_request_hash = await this.generateSHA1Hash(payload);
      return payload;
    },

    getQuoteFeed(rfqId, onQuote, onError, timer = null) {
      const url = `${RFQ_PATH}/quotefeed/${rfqId}`;
      const options = {
        headers: { SECRETKEY: SECRETKEY },
      };
      if (Number.isFinite(timer) && timer > 0) {
        options.heartbeatTimeout = Math.floor(timer);
      }

      const eventSource = new EventSourcePolyfill(url, options);
      eventSource.addEventListener("QUOTE_EVENT", (event) => {
        const data = JSON.parse(event.data);
        onQuote(data);
      });
      eventSource.onerror = (error) => {
        eventSource.close();
        onError(error);
      };
      return eventSource;
    },

    async generateSHA1Hash(data) {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(JSON.stringify(data));
      const hashBuffer = await crypto.subtle.digest("SHA-1", dataBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    },

    async closeQuoteFeed(rfqId) {
      try {
        const url = `${RFQ_PATH}/cancel/${rfqId}`;
        const body = {
          rfq_id: rfqId,
          rfq_venue_id: RFQ_VENUE_ID,
          rfq_venue_secret: RFQ_VENUE_SECRET,
          rfq_cancel_time: Date.now() / 1000,
          rfq_cancel_message: "Taker manually declined quoted price",
        };
        return await axios.post(url, body, {
          headers: { SECRETKEY: SECRETKEY },
        });
      } catch (e) {
        console.error("ERROR closeQuoteFeed: ", e.response?.message, e.response);
      }
    },

    async confirmQuote(quote) {
      try {
        const url = `${RFQ_PATH}/neworder/${quote.rfq_id}`;
        return await axios.post(url, quote, {
          headers: { SECRETKEY: SECRETKEY },
        });
      } catch (e) {
        console.error("ERROR confirmQuote: ", e.response?.message, e.response);
      }
    },

    async formatAndOrder(quote) {
      const newQuote = await this.makeOrderPayload(quote);
      const res = await this.confirmQuote(newQuote);
      return res.data;
    },

    async makeOrderPayload(quote) {
      const payload = {
        rfq_id: quote.rfq_id,
        rfq_venue_id: RFQ_VENUE_ID,
        rfq_venue_secret: RFQ_VENUE_SECRET,
        rfq_order_quote_id: quote.rfq_quote_id,
        rfq_order_px: quote.rfq_quote_px_client,
        rfq_order_time: Date.now() / 1000,
        rfq_order_text: RFQ_TEXT,
      };
      payload.rfq_order_hash = await this.generateSHA1Hash(payload);
      return payload;
    },

    exportOrderlog(activeTab, orderlogFilter) {
      const rows = this.rfqElements({ activeTab, orderlogFilter });
      if (!rows.length) return;
      const headers = [
        "Time",
        "Status",
        "CCY",
        "Side",
        "Amount",
        "Settlement",
        "Maturity",
        "Bank",
        "Spread PTAX",
        "RFQ ID",
      ];
      const lines = [headers.join(",")];
      rows.forEach((r) => {
        lines.push(
          [
            r.rfq_deal_time,
            r.rfq_status,
            r.rfq_ccy,
            r.rfq_side,
            r.rfq_amount,
            r.rfq_spotsett,
            r.rfq_security === "FXNDF" ? r.rfq_maturity : "-",
            r.bank_label,
            r.rfq_spread_ptax,
            r.rfq_id,
          ]
            .map((v) => `"${String(v).replace(/"/g, '""')}"`)
            .join(","),
        );
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orderlog_${new Date().toISOString().substring(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
});
